import { Edit2, Trash2, Search } from "lucide-react";
import { ManageDeal } from "../../services/manageDealsApi";
import { Button } from "../ui/Button";
import { cn } from "../../lib/utils";
import { useAuth } from "../../auth/AuthContext";
import { stripHtml } from "../../lib/stripHtml";

interface DealsTableProps {
  deals: ManageDeal[];
  onEdit: (deal: ManageDeal) => void;
  onDelete: (deal: ManageDeal) => void;
  isLoading?: boolean;
}

export const DealsTable = ({
  deals,
  onEdit,
  onDelete,
  isLoading = false,
}: DealsTableProps) => {
  const { user } = useAuth();
  const currencySymbol =
    user?.company?.currency_symbol ||
    user?.company?.currency?.currency_symbol ||
    "£";

  return (
    <div className="flex-1 rounded-xl lg:border lg:border-gray-200 lg:shadow-sm bg-transparent lg:bg-white overflow-hidden flex flex-col">
      <div className="flex-1 lg:overflow-y-auto relative p-4 lg:p-0">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10 bg-[#885ABB] border-b border-gray-100 hidden lg:table-header-group">
            <tr className="grid grid-cols-12 gap-4 text-sm font-bold text-white tracking-wide px-6 py-4">
              <th className="col-span-4 text-left font-bold">Deal</th>
              <th className="col-span-2 text-left font-bold">Items</th>
              <th className="col-span-2 text-left font-bold">Original</th>
              <th className="col-span-2 text-left font-bold">Deal Price</th>
              <th className="col-span-1 text-left font-bold">Status</th>
              <th className="col-span-1 text-right font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="w-full lg:divide-y lg:divide-gray-100 bg-transparent lg:bg-white flex flex-col lg:table-row-group gap-4 lg:gap-0">
            {isLoading ? (
              <tr className="h-64 w-full flex items-center justify-center lg:table-row">
                <td colSpan={12} className="h-full">
                  <div className="flex h-full w-full items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent" />
                  </div>
                </td>
              </tr>
            ) : deals.length === 0 ? (
              <tr className="flex flex-col items-center justify-center h-64 text-gray-400 w-full lg:table-row">
                <td
                  colSpan={12}
                  className="w-full h-full flex flex-col items-center justify-center lg:table-cell"
                >
                  <div className="flex flex-col items-center justify-center w-full h-full">
                    <Search className="w-12 h-12 mb-3 opacity-20" />
                    <p className="text-lg font-medium text-gray-500">
                      No deals found
                    </p>
                    <p className="text-sm">
                      Add your first deal to sell in Quick Sale
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              deals.map((deal) => {
                const itemsCount = deal.items_count ?? deal.items?.length ?? 0;
                const original = Number(deal.original_amount || 0);
                const dealPrice = Number(deal.deal_amount || 0);
                const hasSaving = original > dealPrice;
                const isActive = (deal.status || "").toLowerCase() === "active";

                return (
                  <tr
                    key={deal.id}
                    className="flex flex-col lg:grid lg:grid-cols-12 gap-4 items-start lg:items-center px-6 py-5 lg:py-4 hover:bg-purple-50/30 transition-colors bg-white rounded-xl lg:rounded-none border border-gray-200 lg:border-0 shadow-sm lg:shadow-none group text-sm w-full relative"
                  >
                    <td className="lg:col-span-4 w-full lg:w-auto">
                      <p className="font-bold lg:font-semibold text-gray-900 lg:text-gray-800 group-hover:text-[#885ABB] transition-colors truncate text-base lg:text-sm">
                        {deal.title}
                      </p>
                      {(() => {
                        const plain = stripHtml(deal.description || "");
                        return plain ? (
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                            {plain}
                          </p>
                        ) : null;
                      })()}
                    </td>

                    <td className="lg:col-span-2 text-gray-700 w-full lg:w-auto flex lg:block justify-between items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                      <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase">
                        Items
                      </span>
                      <span className="font-medium">
                        {itemsCount} {itemsCount === 1 ? "item" : "items"}
                      </span>
                    </td>

                    <td className="lg:col-span-2 text-gray-700 w-full lg:w-auto flex lg:block justify-between items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                      <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase">
                        Original
                      </span>
                      <span
                        className={cn(
                          "font-medium",
                          hasSaving && "line-through text-gray-400 text-[12px]",
                        )}
                      >
                        {currencySymbol}
                        {original.toFixed(2)}
                      </span>
                    </td>

                    <td className="lg:col-span-2 font-bold lg:font-medium text-gray-900 w-full lg:w-auto flex lg:block justify-between items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                      <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase">
                        Deal Price
                      </span>
                      <span className="text-[#885ABB] font-bold">
                        {currencySymbol}
                        {dealPrice.toFixed(2)}
                      </span>
                    </td>

                    <td className="lg:col-span-1 w-full lg:w-auto flex lg:block justify-between items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                      <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase">
                        Status
                      </span>
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold capitalize",
                          isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700",
                        )}
                      >
                        {deal.status || "inactive"}
                      </span>
                    </td>

                    <td className="lg:col-span-1 flex items-center justify-end gap-2 w-full lg:w-auto mt-4 lg:mt-0 pt-4 lg:pt-0 border-t border-gray-100 lg:border-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(deal);
                        }}
                        className="flex-1 lg:flex-none h-10 lg:h-8 px-4 lg:px-2 text-blue-600 border-blue-200 hover:bg-blue-50 gap-2 font-bold lg:font-semibold"
                        title="Edit deal"
                      >
                        <Edit2 size={16} className="lg:w-[15px] lg:h-[15px]" />
                        <span className="lg:hidden">Edit</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(deal);
                        }}
                        className="flex-1 lg:flex-none h-10 lg:h-8 px-4 lg:px-2 text-red-600 border-red-200 hover:bg-red-50 gap-2 font-bold lg:font-semibold"
                        title="Delete deal"
                      >
                        <Trash2 size={16} className="lg:w-[15px] lg:h-[15px]" />
                        <span className="lg:hidden">Delete</span>
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
